// Market inference + the standardized Market / Skills taxonomy for Pearson.
// Market is the prime specialism of a candidate / job (matches users.markt).
// Used by the backfill and (the keyword map) is the single source of truth.

// Top-level markten (= het vakgebied van een consultant; elke gebruiker hoort bij een
// markt, zijn "desk"). App-owned sinds 2026-07-14: markten worden NIET meer uit
// het externe systeem gesynct (taxonomy-sync verwijderd 5 okt 2026). De canonieke markten hieronder zijn
// de desks uit market_owners (single source of truth). De vroegere specialismen
// (.NET, Frontend, PHP, AI, Security, ERP, Embedded, Agile, Techniek, Legacy, Business
// Intelligence, Business Consulting) zijn gedegradeerd tot Skills onder deze markten,
// niet langer eigen markt. Bestaande tags op oude records blijven onaangeroerd.
// CATEGORY_SKILLS hieronder blijft de skill-catalogus voeden; ongebruikte markt-keys
// daarin zijn onschuldig (louter suggesties).
// Legacy heropgevoerd als markt (2026-07-17). Security is opnieuw een officiële,
// eigenaarsgestuurde Markt (2026-07-22). Executive toegevoegd (2026-08-17). Directors kunnen aanvullende Markten via
// het register toevoegen zonder een codewijziging.
import { SLASH_SKILLS, skillDedupeKey } from './skills-key.mjs';
import { withoutJunkSkills } from './skill-junk.mjs';
import { canonicalCategory, categoryKey, CATEGORY_ALIASES } from './category-key.mjs';

export const MARKETS = ['Java', 'Data', 'Cloud', 'Testing', 'Architectuur', 'Finance', 'Controlling', 'Accounting', 'Sales', 'Contracting', 'Legacy', 'Security', 'Executive', 'Business Intelligence'];

// Coerce a free-typed / imported market to the canonical MARKETS casing (case-
// insensitive match), so a stray 'java' collapses into 'Java' instead of spawning
// a new bucket. Values that match no canonical market pass through untouched.
export function normalizeMarket(v) {
  const s = String(v || '').trim();
  if (!s) return s;
  return MARKETS.find((m) => m.toLowerCase() === s.toLowerCase()) || s;
}

// Ordered keyword rules - FIRST MATCH WINS, so specific specialisms precede broad
// role markets (Security before Cloud → "Cloud Security" = Security; Techniek before
// Architectuur → a bouwkundig profile isn't an IT architect; bare "Architect" still
// lands in Architectuur). Applied to the het externe systeem "Markets" token first, then to a
// free-text title as a fallback.
// Commerciele titel: sales (niet Salesforce), account manager/executive, business
// development, marketing. Gebruikt als negatieve vooruitblik in de Data- en BI-regel.
const COMMERCIAL = '(sales(?!force)|commerci|account ?manag|account executive|key account|business develop|marketing|marketeer|marketer|pre-?sales)';
// Een vakrol in de titel (analist, engineer, scientist, developer) blijft data: 'Marketing
// Data Analist' is Data. Anders mag er geen commercieel woord in staan.
const NOT_COMMERCIAL = `(?:(?=.*(analist|analyst|engineer|scientist|developer|architect))|(?!.*${COMMERCIAL}))`;

const RULES = [
  // Legacy first: catches COBOL/mainframe titles (e.g. "Mainframe Engineer") before
  // any broader rule below could otherwise misroute them.
  ['Legacy', [/cobol|mainframe|jcl|rexx|as\/?400|rpg\b/i]],
  // Executive (Markt sinds 17 aug 2026): C-level, directeuren, managing partners
  // en lead-/unitrollen. Voor de vakmarkten, zodat 'CTO' of 'Director Finance'
  // op de executive-desk landt en niet op de discipline in de titel.
  // Commerciele leiding blijft Sales (eigenaarsbesluit 17 aug 2026: Sales is een
  // eigen desk met eigen eigenaar): een titel met sales/commercieel/account/
  // business development/CCO valt hier NIET onder en loopt door naar de Sales-regel.
  // Sinds 15 okt 2026 ook general manager, VP en head of operations (overgenomen uit de
  // oude Vacature-watch-regels, lib/job-watch-markt.mjs).
  // Een adjunct-directeur is Management (eigenaarsbesluit 15 okt 2026).
  ['Executive', [/^(?!.*(sales|commerci|account|business develop|art director|creative director|\bcco\b|adjunct))(?=.*(\bc[eftio]o\b|\bchro\b|head of it|\bdirect(or|eur)\b|managing partner|competence lead|practice lead|unit ?manager|general manager|\bvp\b|head of operations))/]],
  // Management (kleine Markt, eigenaarsbesluit 15 okt 2026): IT- en operationeel
  // management onder directieniveau. Na Executive, zodat head of operations Executive
  // blijft. Commerciele titels lopen door naar Marketing of Sales. Een kale teamleider
  // heeft geen Markt.
  ['Management', [/^(?!.*(sales|commerci|account|business develop|marketing))(?=.*(\b(it|ict)[- ]?manager\b|\bmanager (it|ict)\b|\bhoofd (ict|it)\b|operations manager|adjunct[- ]?directeur|engineering manager|development manager|delivery manager))/]],
  // Recruitment en HR (markten sinds 30 aug 2026, contact-markten ronde):
  // mensen-domeinrollen, vooral op prospect-contactpersonen. NA Executive, zodat
  // een CHRO of HR-directeur op de executive-desk blijft landen (zelfde besluit
  // als CFO -> Executive). Recruitment VOOR HR: een 'HR recruiter' werft.
  ['Recruitment', [/recruit/, /talent acquisition/, /\bsourcer\b/, /werving/, /talent manager/]],
  // Verzuimcoordinator sinds 15 okt 2026; hr business partner, hr adviseur en p&o
  // adviseur vielen al op \bhr\b en \bp&o\b.
  ['HR', [/\bhr\b/, /\bhrm\b/, /human resources?/, /people (& |and |en )?culture/, /\bp&o\b/, /personeelszaken/, /people (partner|manager|officer|ops|operations)/, /verzuimco[oö]rdinator/]],
  ['.NET', [/\.net\b/, /\bdotnet\b/, /\bc#/, /c sharp/, /asp\.net/, /blazor/, /xamarin/, /vb\.net/]],
  // Design en Mobile (kleine Markten, 15 okt 2026) voor Java en Front-end: een
  // UX/UI-designer is Design, geen Front-end; een Kotlin-Android-ontwikkelaar Mobile.
  // Design voor Research: een UX-researcher is Design.
  ['Design', [/\b(ux|ui)\s*\/?\s*(ux|ui)?[- ]?(designer|ontwerper)\b/, /product designer/, /interaction designer/, /visual designer/, /grafisch ontwerper/, /\bux[- ]?researcher\b/]],
  ['Mobile', [/\bios\b/, /\bandroid\b/, /\bmobile (developer|engineer|ontwikkelaar)\b/, /\bflutter\b/, /react native/]],
  ['Java', [/\bjava\b/, /\bkotlin\b/, /spring\s?boot/, /\bspring\b/, /\bjvm\b/, /\bquarkus\b/, /jakarta/, /\bhibernate\b/, /\bscala\b/]],
  ['PHP', [/\bphp\b/, /laravel/, /symfony/, /drupal/, /wordpress/]],
  // 'Front-end' met streepje sinds 7 augustus 2026: dat is de naam in
  // markten_registry, en de inferentie moet dezelfde schrijfwijze opleveren.
  // Deed hij dat niet, dan schreef elke nieuwe inferentie weer een 'Frontend'-rij
  // die de registry niet kent, en werd dat record daarna onbewerkbaar.
  ['Front-end', [/front.?end/, /\bfe\b/, /\breact\b/, /react native/, /\bangular\b/, /\bvue\b/, /\bsvelte\b/, /typescript/, /javascript/, /ux\/ui/, /\bux\b/, /\bui\b/, /\bmobile\b/, /\bios\b/, /android/, /flutter/]],
  ['Embedded', [/embedded/, /\bc\+\+/, /\brtos\b/, /firmware/, /\bvhdl\b/, /\bfpga\b/, /hardware engineer/]],
  ['Security', [/security/, /\bsoc\b/, /\bciso\b/, /pentest/, /\biam\b/, /cyber/, /\bgrc\b/, /\bsiem\b/]],
  // Commerciele titels die een datawoord dragen ('AI-First Solution Sales', 'Account
  // Manager Data', 'Content Marketeer Data & AI') lopen door naar Marketing of Sales
  // (eigenaarsbesluit 30 okt 2026), zoals bij Executive en Vastgoed.
  ['Business Intelligence', [new RegExp(`^${NOT_COMMERCIAL}.*(business intelligence|\\bbi\\b|\\bpower bi\\b|tableau|qlik|looker|\\bdax\\b|\\bssrs\\b|\\bssis\\b|data ?warehouse|\\bdwh\\b)`)]],
  // Oracle (kleine Markt, 15 okt 2026) voor Data, ERP en Software Engineering: een
  // Oracle-DBA, -ontwikkelaar of EBS-consultant is Oracle. Na Business Intelligence.
  ['Oracle', [/\boracle\b/, /\bpl\s*\/?\s*sql\b/]],
  ['Data', [new RegExp(`^${NOT_COMMERCIAL}.*(\\bdata\\b|\\betl\\b|analytics|machine learning|\\bml\\b|\\bai\\b|data scien|data engineer|databricks|snowflake|\\bspark\\b|\\bpython\\b|\\bsql\\b|\\boracle\\b|\\bdba\\b)`)]],
  // Bouw voor Techniek (nieuwe markt 28 aug 2026): uitvoeringsrollen plus de
  // bouwkunde/civiel/gww/bim-woorden die eerst onder Techniek vielen. Techniek
  // houdt werktuigbouw, installatie, elektro en energie.
  // Sinds 29 sep 2026 direct na Data en voor Werkplekbeheer, Cloud en Agile:
  // 'Projectleider civiel' viel op /projectleider/ in Agile en 'Werkvoorbereider
  // infra' op /\binfra\b/ in Cloud (41 kandidaten zonder markt fout ingedeeld).
  // Na Data, zodat een datamodelleur Data blijft.
  ['Bouw', [/uitvoerder/, /werkvoorbereider/, /\bcalculator\b/, /tenderman/, /ontwerpleider/, /modelleur/, /werkverantwoordelijke/, /bouwkund/, /\bbouw\b/, /civiel/, /\bgww\b/, /woningbouw/, /utiliteitsbouw/, /\bbim\b/, /beton/, /\bsloop/, /directievoerder/, /toezichthouder/, /\buitvoering\b/, /\bb&u\b/, /gebiedsontwikkeling/]],
  // Werkplekbeheer voor Cloud: M365/SharePoint/Intune/servicedesk zijn
  // werkplekrollen, geen platform-engineering. Servicedesk is hierheen verhuisd
  // uit de Cloud-regel (eigenaarsbesluit 28 aug 2026).
  // Vastgoed (kleine Markt, 15 okt 2026): na Bouw, zodat bouwrollen Bouw blijven.
  // Commerciele titels lopen door naar Sales.
  ['Vastgoed', [/^(?!.*(sales|commerci|account|business develop|marketing))(?=.*(vastgoed|real estate|property manager|\btaxateur|\bmakelaar|huurbeheer|\bvve[- ]?beheer))/]],
  ['Werkplekbeheer', [/sharepoint/, /\bm365\b/, /(microsoft|office) ?365/, /\bintune\b/, /werkplek/, /modern workplace/, /servicedesk/, /service ?desk/, /endpoint/]],
  // Informatie Management (kleine Markt, 15 okt 2026): voor Cloud en Controlling, zodat
  // een document controller geen controller is.
  ['Informatie Management', [/informatiemanag/, /informatiebeheer/, /document ?controller/, /documentalist/, /records? manager/, /\barchivaris/, /portfolio ?(analist|analyst)/]],
  ['Cloud', [new RegExp(`^${NOT_COMMERCIAL}.*(\\bcloud\\b|\\baws\\b|\\bazure\\b|\\bgcp\\b|devops|kubernetes|\\bk8s\\b|terraform|platform engineer|\\bsre\\b|site reliability|\\blinux\\b|infrastructure|\\binfra\\b|network engineer|network (specialist|consultant|administrator)|system administrator|systeembeheer|\\bnetwerk|sysops|system engineer|\\bsysadmin\\b)`)]],
  ['Testing', [/\btest/, /\bqa\b/, /quality assurance/, /\btester\b/, /\bsdet\b/, /cypress/, /selenium/, /automation engineer/]],
  ['ERP', [/\bsap\b/, /\berp\b/, /dynamics/, /mendix/, /outsystems/, /\bafas\b/, /servicenow/, /salesforce/, /integratie/]],
  // Nederlandse gesloten samenstellingen: 'projectmanager', 'projectleider' en
  // 'projectcoordinator' misten de regel /project manager/ (met spatie) en
  // vielen buiten elke markt (28 aug 2026: 200+ records).
  // Release train engineer sinds 15 okt 2026 (oude Vacature-watch-regels).
  // Product Owner (kleine Markt, 15 okt 2026) voor Agile; product owner is daarom uit
  // de Agile-regel gehaald. De afkorting PO blijft bij Agile.
  ['Product Owner', [/product ?owner/, /product manager/, /product lead/]],
  ['Agile', [/business anal/, /\bba\b/, /\bpo\b/, /scrum/, /\bagile\b/, /\bsafe\b/, /\bpmo\b/, /\bpm\b/, /project manager/, /projectmanager/, /projectleider/, /projectco[oö]rdinat/, /business consultant/, /release train/]],
  ['Techniek', [/techniek/, /werktuigbouw/, /werktuig/, /installatie/, /energietechn/, /elektro/, /electrical/, /\bplm\b/, /\bk&l\b/, /\bhvac\b/, /maintenance engineer/, /\bmonteur\b/]],
  ['Architectuur', [/architect/, /\btogaf\b/, /archimate/]],
  // Fiscalist, aangifte, hoofd administratie, controleleider, boekhouder en
  // salarisadministrateur sinds 15 okt 2026 (oude Vacature-watch-regels).
  // Verzekeringen (kleine Markt, 15 okt 2026): voor Accounting en Finance.
  // Commerciele titels lopen door naar Sales.
  ['Verzekeringen', [/^(?!.*(sales|commerci|account|business develop|marketing))(?=.*(actuari|underwriter|\bacceptant|schadebehandel|claims? handler|verzekering))/]],
  ['Accounting', [/accountan/, /\baccounting\b/, /\bsamenstel/, /\bfiscaal/, /\bfiscalist/, /\baangifte/, /hoofd administratie/, /controleleider/, /\bboekhoud/, /salarisadministr/, /loonadministratie/, /\bgrootboek/, /\baudit/, /\btax\b/, /\bpayroll\b/]],
  ['Controlling', [/controller/, /\bcontrolling\b/, /business control/, /financial control/]],
  ['Finance', [/\bfinanc/, /\bfp&a\b/, /\btreasur/, /corporate finance/, /financial reporting/, /\bifrs\b/]],
  // Marketing (kleine Markt, 15 okt 2026) voor Sales; growth marketeer is daarom uit de
  // Sales-regel gehaald. Kaal 'marketing' blijft bij Sales.
  ['Marketing', [/\bmarketeer\b/, /\bmarketer\b/, /marketing (manager|specialist)/, /\bseo\b/, /\bsea (specialist|marketeer|marketer|consultant|expert|manager)\b/, /performance market/, /growth market/, /communicatie ?adviseur/, /brand manager/, /social media/]],
  // Salesdirecteur, CRO, customer success, growth manager, relatiebeheer, vertegenwoordiger,
  // rayonmanager en partner manager sinds 15 okt 2026 (oude Vacature-watch-regels).
  // 'Salesforce' is een product: die titels landen eerder al in ERP.
  ['Sales', [/\bsales(?!force)/, /business develop/, /account ?manager/, /account director/, /\bcco\b/, /\bcro\b/, /chief revenue/, /\bcommerci/, /\baccount executive\b/, /\binkoop\b/, /\bmarketing\b/, /bid manager/, /customer success/, /\bgrowth manager\b/, /relatiemanag/, /relatiebeheer/, /vertegenwoordig/, /rayonmanag/, /partner ?manager/]],
  ['Contracting', [/\binterim\b/, /\bzzp\b/, /freelance/, /\bcontract/]],
  // Software Engineering als LAATSTE technische regel: het vangnet voor
  // generieke ontwikkeltitels nadat elke specifieke stack (.NET, Java, PHP,
  // Front-end, Embedded, ERP) zijn kans heeft gehad. Bewust GEEN kaal
  // /\bengineer\b/: dat is te breed (techniek, hardware, civiel).
  ['Software Engineering', [/software.?(engineer|developer|ontwikkelaar|ingenieur)/, /\bdeveloper\b/, /\bontwikkelaar\b/, /full.?stack/, /\btech(nical)? lead\b/, /\bprogrammeur\b/]],
  // Research (kleine Markt, 15 okt 2026) als allerlaatste regel: een domeinwoord wint
  // altijd, dus 'PhD Data Scientist' is Data en 'Research Engineer Machine Learning' Data.
  ['Research', [/\bresearcher\b/, /\bonderzoeker\b/, /research scientist/, /\br&d\b/, /wetenschappelijk medewerker/, /\bpostdoc\b/, /\bphd\b/]],
];

// Infer a prime market from a free-text job title / position. Returns null if no rule matches.
export function inferMarket(title) {
  const t = String(title || '').toLowerCase();
  if (!t.trim()) return null;
  for (const [market, pats] of RULES) {
    if (pats.some((p) => p.test(t))) return market;
  }
  return null;
}

// Elke Markt die de titelregels kunnen opleveren, in regelvolgorde.
export const RULE_MARKETS = Object.freeze(RULES.map(([market]) => market));

// Meerdere Markten per kandidaat (eigenaarsbesluit 22 okt 2026): een Test Automation
// Engineer met een Java-stack is Testing en Java. inferMarket hierboven blijft de eerste
// treffer voor wie een Markt nodig heeft; inferMarkets levert de volledige lijst voor de
// automatische schrijvers (CV, Excel-import, Lusha).
//   a. de ENE Markt van de titel (inferMarket, eerste treffer wint, met de vangnetten
//      Software Engineering en Research); nooit meer titelregels tegelijk, want de
//      regelvolgorde is juist wat 'Projectleider civiel' Bouw maakt en niet ook Agile;
//   b. daarna elke Markt waarvan minstens MIN_SKILLS_FOR_MARKET skills van de kandidaat in
//      de skillset van die Markt staan (MARKET_SKILLS), meeste treffers eerst. Alleen een
//      skill die in precies EEN Markt staat telt: gedeeld gereedschap (Excel, Power BI,
//      SAP, JUnit, Python) zegt niets over de Markt.
// Executive en Management komen nooit uit skills: leiding is een titel, geen stack.
// Alleen officiele Markten: opts.known is de levende lijst (liveMarkets); zonder lijst
// telt de zaadlijst plus de titelregels.
export const MIN_SKILLS_FOR_MARKET = 3;
const NO_SKILL_MARKETS = new Set(['Executive', 'Management']);

export function inferMarketsWithEvidence(title, skills = [], { known } = {}) {
  const list = Array.isArray(known) || known instanceof Set ? [...known] : [...MARKETS, ...RULE_MARKETS];
  const canon = new Map(list.map((m) => [String(m || '').trim().toLowerCase(), String(m || '').trim()]).filter(([k]) => k));
  const out = [];
  const seen = new Set();
  const add = (market, via, hits = []) => {
    const name = canon.get(String(market || '').toLowerCase());
    if (!name || seen.has(name.toLowerCase())) return;
    seen.add(name.toLowerCase());
    out.push({ market: name, via, skills: hits });
  };
  const fromTitle = inferMarket(title);
  if (fromTitle) add(fromTitle, 'title');
  const own = new Map();
  for (const s of Array.isArray(skills) ? skills : []) {
    const k = skillDedupeKey(String(s || '').trim());
    if (k && !own.has(k)) own.set(k, String(s).trim());
  }
  if (own.size) {
    const byMarket = [];
    for (const [market, set] of Object.entries(MARKET_SKILLS)) {
      if (NO_SKILL_MARKETS.has(market)) continue;
      const hits = [...own].filter(([k]) => set.has(k) && SKILL_MARKET_COUNT.get(k) === 1).map(([, name]) => name);
      if (hits.length >= MIN_SKILLS_FOR_MARKET) byMarket.push({ market, hits });
    }
    byMarket.sort((a, b) => b.hits.length - a.hits.length);
    for (const { market, hits } of byMarket) add(market, 'skills', hits);
  }
  return out;
}

/** De Markten van een kandidaat uit titel en skills, geordend en uniek (zie hierboven). */
export function inferMarkets(title, skills = [], opts = {}) {
  return inferMarketsWithEvidence(title, skills, opts).map((x) => x.market);
}

/** Is deze waarde de naam van een Markt (de zaadlijst of een titelregel)? Hoofdletterongevoelig. */
export function isMarketName(v) {
  const k = String(v || '').trim().toLowerCase();
  return !!k && [...MARKETS, ...RULE_MARKETS].some((m) => m.toLowerCase() === k);
}

// Curated starter skills per market - seeds the skills_catalog so suggestions
// exist from day one. Users can add new skills, which join the catalog.
export const SKILL_SEED = {
  Java: ['Java', 'Kotlin', 'Spring', 'Spring Boot', 'Hibernate', 'Maven', 'Gradle', 'Microservices', 'REST', 'JUnit', 'Quarkus', 'Jakarta EE', 'Scala', 'Kafka'],
  Data: ['Python', 'SQL', 'ETL', 'Spark', 'Databricks', 'Snowflake', 'Airflow', 'dbt', 'Power BI', 'Tableau', 'Machine Learning', 'TensorFlow', 'Kafka', 'Azure Data Factory'],
  Cloud: ['AWS', 'Azure', 'GCP', 'Kubernetes', 'Docker', 'Terraform', 'Ansible', 'CI/CD', 'Jenkins', 'GitLab', 'Linux', 'Prometheus', 'Helm', 'DevOps', 'SRE'],
  Testing: ['Selenium', 'Cypress', 'Playwright', 'TestNG', 'JUnit', 'Cucumber', 'Postman', 'JMeter', 'ISTQB', 'Test Automation', 'Performance Testing', 'API Testing'],
  Architectuur: ['Enterprise Architecture', 'Solution Architecture', 'TOGAF', 'ArchiMate', 'Microservices', 'Cloud Architecture', 'Integration', 'Domain-Driven Design'],
  Finance: ['Financial Reporting', 'IFRS', 'FP&A', 'Treasury', 'Corporate Finance', 'M&A', 'Excel', 'Power BI', 'SAP', 'AFAS', 'Consolidation'],
  Controlling: ['Controlling', 'Business Control', 'Financial Control', 'Business Controller', 'Financial Controller', 'Budgeting', 'Forecasting', 'Management Reporting', 'Excel', 'Power BI', 'SAP'],
  Accounting: ['Accounting', 'Accountancy', 'Samenstel', 'Audit', 'Fiscaal', 'Tax', 'Salarisadministratie', 'Loonadministratie', 'Payroll', 'Jaarrekening', 'Grootboek', 'AFAS'],
  Sales: ['New Business', 'Account Management', 'Lead Generation', 'CRM', 'Negotiation', 'B2B', 'Pipeline Management', 'Cold Calling'],
  Contracting: ['Interim Management', 'Project Management', 'Freelance', 'Stakeholder Management'],
  Legacy: ['COBOL', 'JCL', 'REXX', 'Mainframe', 'IBM Mainframe', 'z/OS', 'DB2', 'CICS', 'IMS', 'AS/400', 'RPG', 'PL/I', 'IDMS', 'VSAM', 'TSO/ISPF'],
  'Business Intelligence': ['Power BI', 'Tableau', 'DAX', 'SQL', 'SSIS', 'SSRS', 'Qlik', 'Looker', 'Data Warehouse', 'ETL', 'Python', 'Excel'],
};

// ── 3-level taxonomy: Market (top) → Category (mid) → Skills (bottom) ──────────
// The mid level groups a market's specialisms. Seeded from Sydney's Finance model
// (het externe systeem "Markets Contacts" + "Skills"); other markets gain categories as the
// migration off het externe systeem continues. Source of truth during the migration is the
// imported het externe systeem value (stamped source='rcrm' + synced_at); an in-app edit
// flips it to source='app' and is never re-synced over.
// Suggested starter categories + skills per market. Curated proposal - refine in
// app over time. Drives: the editor's category datalist, skill suggestions, and
// best-effort auto-categorisation (a skill only sets its category when it belongs
// to the entity's market). 'Management' is added to every market on top of these.
export const CATEGORY_SKILLS = {
  Java: {
    Webbased: ['Spring Boot', 'Spring MVC', 'REST', 'Microservices', 'Spring WebFlux', 'Thymeleaf', 'Spring Security', 'Spring Data', 'OpenAPI'],
    'Classic Back-end': ['Java SE', 'Jakarta EE', 'JEE', 'EJB', 'Batch', 'JMS', 'Monoliet', 'Hibernate', 'JPA', 'JDBC', 'WebSphere', 'WildFly'],
    'Full-stack': ['React', 'Angular', 'TypeScript'],
    'JVM-talen': ['Kotlin', 'Scala', 'Groovy'],
    'Cloud-native Java': ['Quarkus', 'Micronaut', 'GraalVM', 'Jib', 'Helidon'],
    'Messaging & Data': ['Kafka', 'RabbitMQ', 'PostgreSQL', 'MongoDB', 'Redis', 'Elasticsearch'],
    'Build & Test': ['Maven', 'Gradle', 'JUnit', 'Mockito', 'Testcontainers', 'SonarQube'],
  },
  '.NET': {
    'Web / ASP.NET': ['ASP.NET Core', 'C#', 'Entity Framework', 'Web API', 'Blazor'],
    'Full-stack': ['C#', 'Blazor', 'React', 'Angular', 'TypeScript'],
    Desktop: ['WPF', 'WinForms', '.NET MAUI', 'Xamarin'],
  },
  'Front-end': {
    React: ['React', 'Redux', 'Next.js', 'TypeScript'],
    Angular: ['Angular', 'RxJS', 'NgRx'],
    Vue: ['Vue', 'Nuxt', 'Pinia'],
    Mobile: ['iOS', 'Android', 'Flutter', 'React Native', 'Swift'],
  },
  PHP: {
    Laravel: ['Laravel', 'MySQL', 'Eloquent'],
    Symfony: ['Symfony', 'Doctrine'],
    CMS: ['WordPress', 'Drupal', 'Magento'],
  },
  Data: {
    'Data Engineering': ['Python', 'SQL', 'Spark', 'Databricks', 'Snowflake', 'Airflow', 'dbt', 'Azure Data Factory', 'Delta Lake', 'Microsoft Fabric', 'Azure Synapse', 'BigQuery'],
    'BI & Analytics': ['Power BI', 'Tableau', 'DAX', 'Qlik', 'Looker', 'Power Query'],
    'Data Science / ML': ['Machine Learning', 'TensorFlow', 'PyTorch', 'scikit-learn', 'MLOps', 'R', 'Pandas', 'XGBoost', 'Statistiek'],
    'AI Engineering': ['LLM', 'RAG', 'Generative AI', 'Prompt Engineering', 'LangChain', 'OpenAI', 'Vector Database', 'NLP', 'Azure OpenAI', 'AI Agents', 'Embeddings'],
    'Data Governance': ['Data Quality', 'Master Data Management', 'Data Catalog', 'Collibra', 'DAMA-DMBOK', 'Data Lineage'],
    'Data Architecture': ['Data Modelling', 'Data Vault', 'Lakehouse', 'Data Mesh', 'Kimball'],
  },
  // Placeholder market for later - categories ready for when AI is split from Data.
  AI: {
    'Generative AI': ['LLM', 'RAG', 'Prompt Engineering', 'LangChain', 'OpenAI', 'Fine-tuning'],
    'ML Engineering': ['MLOps', 'Model Deployment', 'TensorFlow', 'PyTorch', 'Kubeflow'],
    'Data Science': ['Machine Learning', 'scikit-learn', 'Experimentation'],
    NLP: ['NLP', 'Transformers', 'spaCy'],
    'Computer Vision': ['Computer Vision', 'OpenCV', 'Detectie'],
  },
  Cloud: {
    'Cloud / Platform': ['AWS', 'Azure', 'GCP', 'Terraform', 'Cloud Architecture', 'Azure DevOps', 'OpenShift', 'Bicep', 'CloudFormation'],
    'DevOps / SRE': ['Kubernetes', 'Docker', 'CI/CD', 'Jenkins', 'GitLab', 'Ansible', 'Helm', 'Prometheus', 'GitHub Actions', 'Argo CD', 'Grafana', 'OpenTelemetry'],
    Networking: ['Cisco', 'Routing', 'Switching', 'Firewalls', 'SD-WAN', 'Palo Alto', 'Fortinet', 'Juniper', 'BGP', 'Load Balancing'],
    Systeembeheer: ['Windows Server', 'Active Directory', 'Microsoft 365', 'Linux', 'VMware', 'Intune', 'PowerShell', 'Citrix'],
    'Platform Engineering': ['Internal Developer Platform', 'Backstage', 'GitOps', 'Crossplane', 'Service Mesh'],
    FinOps: ['Cloud Cost Management', 'FinOps Framework', 'Cost Allocation', 'Reserved Instances', 'Showback'],
  },
  Security: {
    AppSec: ['Pentesting', 'OWASP', 'Secure Coding', 'DevSecOps', 'SAST', 'DAST', 'Threat Modelling', 'Burp Suite'],
    'Cloud Security': ['Azure Security', 'AWS Security', 'CSPM', 'Zero Trust', 'Defender for Cloud', 'CNAPP'],
    'GRC / Compliance': ['ISO 27001', 'NIS2', 'Privacy', 'Risk', 'Compliance', 'BIO', 'DORA', 'NEN 7510'],
    'SOC / Blue Team': ['SIEM', 'SOAR', 'Incident Response', 'Threat Hunting', 'Splunk', 'Microsoft Sentinel', 'EDR', 'CrowdStrike'],
    IAM: ['Identity', 'Okta', 'Entra ID', 'SailPoint', 'CyberArk', 'PAM', 'SSO', 'MFA'],
    'OT security': ['OT Security', 'IEC 62443', 'SCADA', 'ICS', 'Netwerksegmentatie'],
    Cryptography: ['PKI', 'HSM', 'TLS', 'Key Management', 'Post-quantum Cryptography'],
    'Offensive Security': ['Red Teaming', 'OSCP', 'Social Engineering', 'Exploit Development'],
  },
  Testing: {
    'Test Automation': ['Selenium', 'Cypress', 'Playwright', 'TestNG', 'Cucumber', 'API Testing', 'Robot Framework', 'REST Assured', 'Postman', 'Test Automation'],
    'Functioneel / Manueel': ['ISTQB', 'Testcases', 'Exploratory Testing', 'Acceptatietesten', 'Regressietesten', 'Jira', 'Xray', 'Testdata'],
    Performance: ['JMeter', 'Gatling', 'Load Testing', 'k6', 'LoadRunner', 'Performance Testing'],
    'Mobile testing': ['Appium', 'Espresso', 'XCUITest', 'BrowserStack', 'Device Farm'],
    'Security testing': ['OWASP ZAP', 'Penetratietest', 'Vulnerability Scanning'],
    'Test management': ['Teststrategie', 'Testplan', 'TMap', 'Risk-based Testing', 'Testcoordinatie'],
  },
  Architectuur: {
    Enterprise: ['TOGAF', 'ArchiMate', 'Enterprise Architecture', 'Application Portfolio Management', 'LeanIX', 'Roadmapping'],
    Domain: ['Domain-Driven Design', 'Bounded Contexts', 'Event Storming', 'Context Mapping'],
    Solutions: ['Solution Architecture', 'Integration', 'Microservices', 'API Design', 'Event-driven Architecture'],
    Cloud: ['Cloud Architecture', 'Well-Architected', 'Azure Architecture', 'AWS Architecture', 'Multi-cloud'],
    Software: ['Software Architecture', 'Design Patterns', 'Clean Architecture', 'Hexagonal Architecture', 'C4 Model'],
    Business: ['Business Architecture', 'Capability Modelling', 'Value Streams', 'BIZBOK'],
    ERP: ['ERP Integration', 'SAP Architecture'],
    Competence: ['Architecture Governance', 'Mentoring', 'Architecture Board'],
    Security: ['Security Architecture', 'Threat Modelling', 'SABSA', 'Zero Trust'],
    Data: ['Data Architecture', 'Data Modelling', 'Data Mesh', 'Lakehouse'],
  },
  ERP: {
    SAP: ['ABAP', 'S/4HANA', 'SAP FICO', 'SAP MM'],
    Oracle: ['Oracle EBS', 'Oracle Fusion', 'PL/SQL'],
    'Microsoft Dynamics': ['Dynamics 365', 'Business Central', 'Power Platform'],
    'Low-code': ['Mendix', 'OutSystems', 'Power Apps'],
    ServiceNow: ['ITSM'],
  },
  Embedded: {
    Firmware: ['C', 'RTOS', 'Bare-metal'],
    'Embedded Linux': ['Yocto', 'Device Drivers', 'Buildroot'],
    'FPGA / Hardware': ['VHDL', 'Verilog', 'FPGA', 'PCB'],
  },
  Agile: {
    'Business Analyse': ['Requirements', 'BPMN', 'User Stories', 'UML'],
    'Product Ownership': ['Backlog', 'Roadmap', 'Stakeholder Management'],
    'Scrum / Agile Coaching': ['Scrum', 'SAFe', 'Kanban', 'Agile Coaching'],
    Projectmanagement: ['Prince2', 'PMP', 'Planning', 'Risk Management'],
  },
  Techniek: {
    Elektrotechniek: ['PLC', 'Aandrijftechniek', 'Schema\'s', 'Meet- & regeltechniek'],
    Werktuigbouwkunde: ['CAD', 'SolidWorks', 'Tekenwerk', 'Engineering'],
    'Civiele techniek / GWW': ['Calculatie', 'Uitvoering', 'Projectvoorbereiding'],
    Bouwkunde: ['BIM', 'Revit', 'Werkvoorbereiding'],
    Installatietechniek: ['E&W', 'Werktuigbouwkundig', 'Onderhoud'],
    Energietechniek: ['Duurzaamheid', 'Hoogspanning', 'Netbeheer'],
  },
  Finance: {
    'Corporate finance': ['FP&A', 'Treasury', 'Corporate Finance', 'M&A', 'Business Case', 'Valuation', 'Financial Modelling', 'Investment Analysis'],
    Reporting: ['Financial Reporting', 'IFRS', 'Consolidation', 'Dutch GAAP', 'US GAAP', 'XBRL', 'Year-end Closing', 'ESG Reporting', 'CSRD'],
    Treasury: ['Cash Management', 'Liquidity Planning', 'Hedging', 'Cash Pooling', 'Bank Relations', 'Kyriba'],
    Tax: ['Corporate Income Tax', 'VAT', 'Transfer Pricing', 'Tax Control Framework', 'Pillar Two'],
    'Credit risk': ['Credit Risk Modelling', 'IFRS 9', 'Basel', 'Kredietbeoordeling', 'Risk Management', 'SAS'],
  },
  Controlling: {
    Controlling: ['Controlling', 'Budgeting', 'Management Reporting', 'Variance Analysis', 'Kostprijsberekening'],
    'Business control': ['Business Control', 'Business Controller', 'Business Partnering', 'Business Cases', 'KPI-dashboards'],
    'Financial control': ['Financial Control', 'Financial Controller', 'Maandafsluiting', 'Interne beheersing', 'SOx'],
    'Planning & Control': ['P&C-cyclus', 'Begroting', 'Rolling Forecast', 'Kwartaalrapportage', 'Prognoses'],
    'FP&A tools': ['Anaplan', 'Jedox', 'OneStream', 'Board International', 'TM1'],
  },
  Accounting: {
    Samenstel: ['Samenstel', 'NV COS 4410'],
    Audit: ['Audit', 'Controleverklaring', 'IT-audit', 'Caseware'],
    Fiscaal: ['Fiscaal', 'Tax', 'Vennootschapsbelasting', 'Omzetbelasting', 'Inkomstenbelasting'],
    Salarisadministratie: ['Salarisadministratie', 'Loonadministratie', 'Payroll', 'Nmbrs', 'Loket.nl', 'Loonheffingen'],
    Accounting: ['Accountancy', 'Accounting', 'Jaarrekening', 'Grootboek', 'Boekhouding', 'Debiteurenbeheer', 'Crediteurenbeheer', 'Btw-aangifte'],
    Software: ['Exact', 'Twinfield', 'Nmbrs', 'Caseware', 'AFAS', 'Unit4', 'Visma'],
    Standaarden: ['NV COS 2400', 'NV COS 4410', 'XBRL', 'SBR', 'Titel 9 BW2', 'RJ'],
  },
  Sales: {
    'New Business': ['Acquisitie', 'Lead Generation', 'Cold Calling', 'Prospecting', 'Outbound', 'LinkedIn Sales Navigator'],
    'Account Management': ['Relatiebeheer', 'Upsell', 'Key Accounts', 'Account Planning', 'Cross-sell', 'Renewals'],
    Recruitment: ['Sourcing', 'Werving & Selectie', 'Intake', 'Talent Acquisition', 'Arbeidsmarktcommunicatie'],
    Marketing: ['Online Marketing', 'Content', 'Campagnes', 'SEO', 'SEA', 'Marketing Automation', 'Social Media'],
    'Pre-sales / Solution Sales': ['Solution Selling', 'Productdemo', 'RFP', 'Proof of Concept', 'Value Selling', 'Bid Management'],
    'Inside Sales': ['Inside Sales', 'Leadkwalificatie', 'SDR', 'BDR', 'Sales Cadence'],
    'SaaS sales': ['SaaS', 'ARR', 'Churn', 'MEDDIC', 'Land and Expand', 'Subscription Sales'],
    'CRM tools': ['Salesforce', 'HubSpot', 'Dynamics 365 Sales', 'Pipedrive'],
  },
  Contracting: {
    'Interim Management': ['Change Management', 'Transitie', 'Crisismanagement', 'Turnaround', 'Kwartiermaker'],
    Freelance: ['ZZP', 'Detachering', 'Modelovereenkomst', 'Schijnzelfstandigheid'],
    'Inhuur & Broker': ['Inhuurdesk', 'Brokerplatform', 'Aanbesteding', 'Raamovereenkomst', 'DAS'],
    Payrolling: ['Payrolling', 'Uitzenden', 'ABU-cao', 'NBBU-cao'],
  },
  // Executive (17 aug 2026): de leidinggevende desk. Zeven functionele
  // categorieen naar het model van executive search (per functie, niet per
  // technologie); commerciele leiding staat bewust bij Sales.
  Executive: {
    'Algemeen management': ['CEO', 'Algemeen directeur', 'Managing Director', 'Managing Partner', 'DGA', 'Business Unit Manager', 'MT-lid', 'P&L-verantwoordelijkheid', 'Strategie', 'Governance', 'Stakeholdermanagement'],
    'Technologie & IT': ['CTO', 'CIO', 'Head of IT', 'IT-directeur', 'Technical Director', 'IT-strategie', 'Digitale transformatie', 'Portfoliomanagement', 'Sourcing & Outsourcing', 'IT Governance', 'Enterprise Architecture', 'CDO'],
    Operations: ['COO', 'Operationeel directeur', 'Operations Director', 'Supply Chain', 'Procesoptimalisatie', 'Lean / Six Sigma', 'Change Management', 'Programmamanagement'],
    Commercieel: ['CCO', 'Commercieel directeur', 'Go-to-market', 'Pricing', 'Partnerships', 'Business Development'],
    Finance: ['CFO', 'Financieel directeur', 'Finance Director', 'Controlling', 'Treasury', 'M&A', 'Investor Relations', 'IFRS'],
    'Practice & competence leadership': ['Practice Lead', 'Competence Lead', 'Chapter Lead', 'Head of Engineering', 'VP Engineering', 'Delivery Director', 'Resourcemanagement', 'Coaching', 'Talentontwikkeling', 'Consultancy'],
    'People & HR': ['CHRO', 'HR-directeur', 'People Director', 'Organisatieontwikkeling', 'Talent Management', 'Arbeidsvoorwaarden', 'Cultuur & Leiderschap'],
    'Interim executive': ['Interim CFO', 'Interim CIO', 'Interim CTO', 'Interim COO', 'Interim CEO', 'Turnaround'],
    Toezicht: ['RvC', 'RvT', 'Commissaris', 'Board Advisory', 'Corporate Governance Code'],
  },
  Legacy: {
    'Mainframe development': ['COBOL', 'RPG', 'PL/I', 'CICS', 'REXX', 'IDMS', 'IMS', 'DB2', 'Assembler', 'Easytrieve', 'Endevor', 'ChangeMan'],
    'Mainframe operations': ['JCL', 'z/OS', 'AS/400', 'VSAM', 'TSO/ISPF', 'Mainframe', 'IBM Mainframe', 'RACF', 'CA-7', 'Control-M', 'SDSF'],
    'Natural / Adabas': ['Natural', 'Adabas', 'EntireX', 'Software AG'],
    Modernisering: ['Mainframe Modernization', 'Rehosting', 'Micro Focus', 'API-enablement'],
  },
  'Business Intelligence': {
    'Reporting & Dashboards': ['Power BI', 'Tableau', 'Qlik', 'Looker', 'SSRS', 'DAX', 'Microsoft Fabric', 'Paginated Reports', 'MicroStrategy'],
    'Data Warehousing': ['Data Warehouse', 'DWH', 'SSIS', 'ETL', 'Dimensional Modeling', 'Star Schema', 'SSAS', 'Azure Synapse', 'Microsoft Fabric', 'Data Vault', 'Snowflake'],
    Analytics: ['Business Analytics', 'Data Analytics', 'KPI', 'Forecasting', 'Power Query', 'Excel', 'Self-service BI'],
    'Semantic modelling': ['SSAS', 'Tabular Model', 'DAX', 'OLAP', 'Power BI Semantic Model'],
  },
};

// De skillset per Markt voor inferMarkets (22 okt 2026): de twee zaadlijsten van dit
// bestand samen, SKILL_SEED en de categorieen van CATEGORY_SKILLS, op skillDedupeKey.
// Een skill mag in meer Markten staan (JUnit is Java en Testing). Geen andere bron.
export const MARKET_SKILLS = (() => {
  const out = {};
  const put = (market, skills) => {
    const set = out[market] || (out[market] = new Set());
    for (const s of skills) { const k = skillDedupeKey(String(s || '').trim()); if (k) set.add(k); }
  };
  for (const [market, skills] of Object.entries(SKILL_SEED)) put(market, skills);
  for (const [market, cats] of Object.entries(CATEGORY_SKILLS)) for (const skills of Object.values(cats)) put(market, skills);
  return Object.freeze(out);
})();

// In hoeveel Markten van MARKET_SKILLS een skillsleutel staat; inferMarkets telt alleen 1.
const SKILL_MARKET_COUNT = (() => {
  const n = new Map();
  for (const set of Object.values(MARKET_SKILLS)) for (const k of set) n.set(k, (n.get(k) || 0) + 1);
  return n;
})();

// Skills zijn los van de Markt (eigenaarsbesluit 14 okt 2026): een skill impliceert
// nooit een Markt, dus hier valt niets weg op Markt. Alleen rommel (withoutJunkSkills)
// en dubbelen (skillDedupeKey) gaan eruit. Het tweede argument telt niet meer en blijft
// alleen voor de bestaande aanroepers.
export function trimSkills(skills) {
  const seen = new Set();
  return withoutJunkSkills((skills || []).map((s) => String(s == null ? '' : s).trim()).filter(Boolean))
    .filter((s) => { const k = skillDedupeKey(s); if (!k || seen.has(k)) return false; seen.add(k); return true; });
}

// A cross-cutting category every market offers: a management / lead role within
// that market (so a Job or candidate can be flagged management-type). Auto-detected
// from the title and always offered in the editor.
export const UNIVERSAL_CATEGORIES = ['Management'];
// Een echte leidinggevende titel (eigenaarsbesluit 15 okt 2026): manager, lead, head,
// director, directeur, C-level (ook 'chief ...'), teamlead, hoofd (ook hoofduitvoerder),
// projectleider, leidinggevende, eigenaar, owner en founder van een bedrijf (geen product-,
// epic- of data-owner), ook als samenstelling (projectmanager, teammanager). Alleen de titel telt:
// een Markets-token of skill als 'Management' of 'Project management' maakt niemand
// leidinggevend, en een kaal 'management' in de titel (Management assistant, Asset
// management consultant) ook niet. Account-, business-development-, new-business-,
// customer-success-, partner- en channelmanagers zijn commerciele rollen zonder team.
const MANAGEMENT_PATS = [/managers?\b/, /\blead\b(?! ?gen)/, /teamlead/, /teamleid/, /\bhead\b/, /\bhoofd\b/, /\bdirect(or|eur)\b/, /\bc(e|t|i|f|o|d|is)o\b/, /\bchro\b/, /leidinggevend/, /\bvp\b/, /\bsupervisor\b/, /managing partner/,
  /\bchief\b/, /\b(co-?)?founder\b/, /\boprichter\b/, /\beigenaar\b/, /(?<!(product|epic|process|proces|data|service|system|systeem|application|applicatie|technical|technisch) )\bowner\b/, /\bhoofd/, /projectleid/];
const IC_MANAGER = /\b(key\s*)?account(\s+and\s+\w+)?\s*-?\s*manager|business\s*development\s*manager|new\s*business\s*manager|customer\s*success\s*manager|partner\s*manager|channel\s*manager/;
/** Is deze functietitel een echte leidinggevende rol (categorie Management)? */
export function isManagementTitle(title) {
  const t = String(title || '').toLowerCase();
  if (!t.trim() || !MANAGEMENT_PATS.some((p) => p.test(t))) return false;
  // Een commerciele managertitel telt alleen mee als er nog een leidinggevend woord staat.
  if (!IC_MANAGER.test(t)) return true;
  return MANAGEMENT_PATS.some((p) => p.test(t.replace(new RegExp(IC_MANAGER.source, 'g'), ' ')));
}

// Categorie -> skills zonder markt erboven (eigenaarsbesluit 15 okt 2026): de seed voor
// de elf kleine Markten (Mobile, Verzekeringen, Vastgoed, Product Owner, Design, HR,
// Informatie Management, Management, Marketing, Oracle, Research). Een nieuwe categorie
// komt hier, nooit meer onder een markt in CATEGORY_SKILLS. Een categorie die al bestaat
// (ook als alias, lib/category-key.mjs) staat er niet: Product Ownership is PO.
export const FLAT_CATEGORY_SKILLS = Object.freeze({
  iOS: ['Swift', 'SwiftUI'],
  Android: ['Kotlin', 'Jetpack Compose'],
  'Cross-platform': ['Flutter', 'React Native'],
  Actuariaat: ['Solvency II', 'IFRS 17', 'Prophet'],
  Schadebehandeling: [],
  Acceptatie: ['Solvency II'],
  Vastgoedbeheer: ['Yardi', 'Planon', 'Huurbeheer'],
  Taxatie: [],
  'Asset Management': ['NEN 2767', 'Planon'],
  'Product Management': ['Backlog management', 'Roadmapping', 'User stories', 'Jira'],
  UX: ['User research', 'Prototyping', 'Figma'],
  UI: ['Figma', 'Sketch', 'Adobe XD'],
  'Product Design': ['Figma', 'Prototyping', 'User research'],
  'HR Advies': ['Arbeidsrecht', 'Verzuimbegeleiding', 'AFAS'],
  'HR Business Partner': ['Workday', 'AFAS', 'Arbeidsrecht'],
  Informatiebeheer: ['Archiefwet', 'TMLO', 'Corsa', 'SharePoint', 'Portfoliomanagement'],
  'Document Control': ['SharePoint'],
  'IT Management': ['ITIL', 'Budgetbeheer', 'Vendor management'],
  'Operations Management': ['Budgetbeheer', 'Vendor management'],
  'Online Marketing': ['SEO', 'SEA', 'Google Analytics'],
  'Content Marketing': ['HubSpot', 'SEO'],
  Communicatie: [],
  'Oracle Development': ['PL/SQL', 'Oracle APEX'],
  'Oracle DBA': ['Oracle Database'],
  'Oracle EBS': ['Oracle EBS'],
  'R&D': ['MATLAB', 'Experimental design'],
  'Wetenschappelijk onderzoek': ['R', 'Statistiek', 'Experimental design'],
});

// Categorieen en skills hangen niet onder een markt (eigenaarsbesluit 14 okt 2026).
// CATEGORY_SKILLS hierboven blijft de bron van de zaadlijst, maar wie hem leest, leest
// hem plat: SEED_CATEGORY_SKILLS is categorie -> skills over alle markten, samengevoegd
// op categoryKey (lib/category-key.mjs), voor de titelherkenning en als startlijst.
export const SEED_CATEGORY_SKILLS = (() => {
  const out = {};
  const nameOf = new Map();
  for (const cats of Object.values(CATEGORY_SKILLS)) {
    for (const [cat, skills] of Object.entries(cats)) {
      const k = categoryKey(cat);
      if (!k) continue;
      if (!nameOf.has(k)) { nameOf.set(k, canonicalCategory(cat)); out[nameOf.get(k)] = []; }
      const list = out[nameOf.get(k)];
      for (const s of skills) if (!list.some((x) => x.toLowerCase() === String(s).toLowerCase())) list.push(s);
    }
  }
  for (const [cat, skills] of Object.entries(FLAT_CATEGORY_SKILLS)) {
    const k = categoryKey(cat);
    if (!k) continue;
    if (!nameOf.has(k)) { nameOf.set(k, canonicalCategory(cat)); out[nameOf.get(k)] = []; }
    const list = out[nameOf.get(k)];
    for (const s of skills) if (!list.some((x) => x.toLowerCase() === String(s).toLowerCase())) list.push(s);
  }
  return Object.freeze(out);
})();

// De categorieopties: EEN globale lijst (de zaadcategorieen plus de universele),
// welke markt een record ook heeft. Het argument (de markt, van voor 14 okt 2026) telt
// niet meer. Gebruikt door de keuzelijst van de editor; vrije tekst blijft mogelijk.
export function categoriesFor() {
  const seen = new Set();
  return [...Object.keys(SEED_CATEGORY_SKILLS), ...UNIVERSAL_CATEGORIES].filter((c) => {
    const k = categoryKey(c);
    if (seen.has(k)) return false;
    seen.add(k); return true;
  });
}

// Company classification (separate from market) - synced + normalised from the
// het externe systeem "Type bedrijf" field (messy: In-House/Inhouse/… → Eindgebruiker).
export const COMPANY_TYPES = ['Eindgebruiker', 'Consultancy'];
export function normalizeCompanyType(raw) {
  const t = String(raw || '').toLowerCase();
  if (/in.?house|inhouse|in house|eindgebruik|end.?user/.test(t)) return 'Eindgebruiker';
  if (/consultan|detacher/.test(t)) return 'Consultancy';
  return '';
}

// Reverse index: lowercased skill/token → { market, category }. First defining
// category wins (Accounting before Controlling for shared tokens like "Audit"). `market`
// is alleen de herkomst in de zaadlijst; een categorie hangt niet onder een markt, dus
// geen schrijver toetst er nog op (14 okt 2026).
const SKILL_TO_CAT = (() => {
  const m = {};
  for (const [market, cats] of Object.entries(CATEGORY_SKILLS)) {
    for (const [category, skills] of Object.entries(cats)) {
      for (const s of skills) { const k = s.toLowerCase(); if (!m[k]) m[k] = { market, category }; }
    }
  }
  // De platte seed na de oude: een skill die al een categorie had, houdt die.
  for (const [category, skills] of Object.entries(FLAT_CATEGORY_SKILLS)) {
    for (const s of skills) { const k = s.toLowerCase(); if (!m[k]) m[k] = { market: '', category }; }
  }
  return m;
})();

// Look up a single skill/token in the reverse index. Returns { market, category }
// or undefined. Used by the editor's display grouping (MarketSkillsEditor) to
// place curated taxonomy skills (incl. non-IT) before the keyword fallback runs.
export function skillCategoryFor(token) {
  return SKILL_TO_CAT[String(token || '').toLowerCase().trim()] || undefined;
}

// Split a het externe systeem multiselect ("a,b") or free-text skills field ("a; b / c")
// into clean, de-duplicated tokens. Een '/' splitst niet binnen haakjes
// ("Continuous Integration and Continuous Delivery (CI/CD)") en niet in een skill uit
// SLASH_SKILLS (CI/CD, TCP/IP, A/B testing); 'AWS/GCP' blijft twee skills.
const SLASH_PAIRS = SLASH_SKILLS.map((sk) => {
  const [l, r] = sk.toLowerCase().split('/');
  return [l.split(' ').pop(), r.split(' ')[0]];
});
function isSlashSkill(left, right) {
  const l = (left.match(/[A-Za-z0-9]+$/) || [''])[0].toLowerCase();
  const r = (right.match(/^[A-Za-z0-9]+/) || [''])[0].toLowerCase();
  return !!l && !!r && SLASH_PAIRS.some(([a, b]) => a === l && b === r);
}
function splitSkillText(text) {
  const out = [];
  let cur = '';
  let depth = 0;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '(') depth++;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    const sep = ch === ',' || ch === ';' || ch === '|' || (ch === '/' && !isSlashSkill(cur, text.slice(i + 1)));
    if (sep && depth === 0) { out.push(cur); cur = ''; } else cur += ch;
  }
  out.push(cur);
  return out;
}
export function parseTokens(str) {
  const seen = new Set();
  return splitSkillText(String(str || '').replace(/Samenstel\.\s*Audit/gi, 'Samenstel, Audit'))
    .map((s) => s.trim()).filter(Boolean)
    .filter((s) => { const k = s.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; });
}

// Resolve { market, category, skills[] } for an entity from its imported
// "Markets" multiselect + a free-text skills field + a title.
// market: uitsluitend uit de titel via inferMarket (eigenaarsbesluit 14 okt 2026: een
//   skill of categorie impliceert nooit een Markt). Wie een expliciet Markt-veld heeft,
//   roept inferMarket zelf op dat veld aan.
// category: Management bij een leidinggevende titel (isManagementTitle), anders de zaadcategorie van de
//   eerste bekende skill (of Markets-token), anders het eerste Markets-token. Een
//   skilltoken zonder zaadcategorie wordt nooit de categorie.
// skills: the skills-field tokens (falling back to the markets tokens).
export function resolveTaxonomy(input = {}) {
  const { marketsValue = '', skillsText = '', title = '' } = input || {};
  const marketToks = parseTokens(marketsValue);
  const skillToks = parseTokens(skillsText);
  const all = [...marketToks, ...skillToks];
  const market = (title && inferMarket(title)) || '';
  let category = '';
  // A management/lead role is the category (cross-cutting), regardless of market.
  if (isManagementTitle(title)) category = 'Management';
  // A curated category from the seed list, whatever the market (14 Oct 2026: categories
  // do not belong to a market). Prefer the actual Skills field over the broad imported
  // market token. This preserves Sydney's Accounting specialisms such as Samenstel and
  // Audit.
  if (!category) for (const t of [...skillToks, ...marketToks]) { const sc = SKILL_TO_CAT[t.toLowerCase()]; if (sc) { category = sc.category; break; } }
  // Een Markets-token 'Management' is geen leidinggevende titel (besluit 15 okt 2026).
  if (!category && categoryKey(marketToks[0]) !== 'management') category = marketToks[0] || '';
  const skills = (skillToks.length ? skillToks : marketToks);
  return { market, category, skills };
}

// De categorie uit een functietitel (eigenaarsbesluit 15 okt 2026), voor een record
// zonder categorie. Volgorde, eerste treffer wint:
//   1. een zaadcategorie of haar alias staat letterlijk in de titel (langste eerst):
//      'Data Engineer' is Data Engineering, 'Test Automation Engineer' Test Automation;
//   2. een zaadskill van minstens drie tekens staat in de titel: de categorie van die skill;
//   3. de Markt van de titel (inferMarket) als er een categorie met die naam bestaat:
//      'Java Developer' is Java.
// Management komt hier nooit uit; dat is isManagementTitle. known: optioneel een Set van
// categoryKeys (het register); een categorie daarbuiten telt dan niet. Leeg als niets past.
const TITLE_CATEGORY_NAMES = (() => {
  const names = new Set();
  for (const c of Object.keys(SEED_CATEGORY_SKILLS)) {
    names.add(c);
    for (const a of CATEGORY_ALIASES[canonicalCategory(c)] || []) names.add(a);
  }
  return [...names].filter((n) => /[a-z0-9]/i.test(n)).sort((a, b) => b.length - a.length);
})();
const TITLE_SKILLS = Object.keys(SKILL_TO_CAT).filter((k) => k.length >= 3).sort((a, b) => b.length - a.length);
const escRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const inTitle = (t, word) => new RegExp(`(^|[^a-z0-9])${escRe(String(word).toLowerCase())}([^a-z0-9]|$)`).test(t);
// Na de zaadnamen: ontwikkelrichtingen die als los woord in de titel staan.
const TITLE_DEV_CATEGORY = [
  [/\b(azure|aws|gcp|google\s*cloud)\b/, 'Cloud'],
  [/\bfront\s*-?\s*end\b/, 'Front-end'],
  [/\bback\s*-?\s*end\b/, 'Back-end'],
  [/\bfull\s*-?\s*stack\b/, 'Full-stack'],
  [/(^|[^a-z])c#|\.net\b|\bdotnet\b/, '.NET'],
];
const TITLE_ROLE_CATEGORY = [
  [/\bbusiness\s*analy(st|ist|se)\b/, 'Business Analist'],
  [/\bnetwork\s*(engineer|developer|specialist|architect|admin\w*)\b|\bnetwerk\w*\b/, 'Networking'],
  [/\belectronic\w*\b|\belektronica\b|\belektrotechn\w*\b|\bhardware\s*(engineer|developer|ontwikkelaar)\b/, 'Elektrotechniek'],
  [/\binkop(er|en)\b|\binkoop\b|\bprocurement\b|\bpurchas(er|ing)\b|\bsourcing\s*(&|en|and)\s*contract\b|\bcontract\s*manager\b/, 'Inkoop'],
  [/\btester\b|\btestanalist\b/, 'Testing'],
  [/\bproject\s*-?\s*(leider|manager)\b|\bprojectleider\b|\bprojectmanager\b|\bpmo\b|\bprogram(ma|me)?\s*-?\s*manager\b/, 'Project Management'],
  [/\bsysteembeheer(der)?\b|\bsystem\s*administrator\b|\bsysadmin\b/, 'Systeembeheer'],
];
export function categoryFromTitle(title, { known, names = [] } = {}) {
  // De werkgever achter 'bij', 'at' of '@' hoort niet bij de functie: 'Infrastructure
  // Engineer bij Info Support B.V.' is geen Support.
  const t = String(title || '').toLowerCase().replace(/\s+/g, ' ').replace(/\s(bij|at|@)\s.*$/, '').trim();
  if (!t) return '';
  const ok = (c) => {
    const k = categoryKey(c);
    return !!k && k !== 'management' && (!known || known.has(k));
  };
  // 0. vaste rolwoorden gaan voor (eigenaarsbesluit 15 okt 2026): 'Software Tester' is
  //    Testing, niet Software; een projectleider of PMO is Project Management.
  for (const [re, c] of TITLE_ROLE_CATEGORY) if (re.test(t) && ok(c)) return canonicalCategory(c);
  // Een accountmanager is Sales, tenzij de titel ook een technische rol noemt.
  if (/\b(key\s*)?account\s*-?\s*manager\b|\baccountmanager\b|\baccount\s*executive\b/.test(t)
    && !/\b(architect|engineer|developer|consultant|ontwikkelaar)\b/.test(t) && ok('Sales')) return canonicalCategory('Sales');
  // De Markt van de titel is GEEN categorie meer (eigenaarsbesluit 15 okt 2026):
  // inferMarket maakte van een projectleider Agile en van een systeembeheerder Cloud.
  // Eerst een zaadnaam, dan een ontwikkelrichting, dan een registernaam (langste eerst),
  // pas daarna een skill.
  for (const n of TITLE_CATEGORY_NAMES) if (inTitle(t, n) && ok(n)) return canonicalCategory(n);
  for (const [re, c] of TITLE_DEV_CATEGORY) if (re.test(t) && ok(c)) return canonicalCategory(c);
  for (const n of [...names].filter((x) => String(x).length >= 3).sort((a, b) => b.length - a.length)) if (inTitle(t, n) && ok(n)) return n;
  for (const s of TITLE_SKILLS) if (inTitle(t, s) && ok(SKILL_TO_CAT[s].category)) return canonicalCategory(SKILL_TO_CAT[s].category);
  // Terugval: een ontwikkelaarstitel zonder vakgebied is Software, een accountmanager Sales.
  // 'Ontwikkelaar' alleen met software ervoor: een gebieds- of economisch ontwikkelaar is geen Software.
  if (/\bdeveloper\b|\bsoftware\s*-?\s*ontwikkelaar\b|\bprogrammeur\b/.test(t) && ok('Software')) return canonicalCategory('Software');
  return '';
}
